import produce, {original} from 'immer';
import isEqual from 'react-fast-compare';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {FormDefinitionQuery} from 'forms/form-definition/__generated/FormDefinitionQuery';
import {
  uuid,
  Action,
  BNCH_TYPE_DEF_TO_FORM_FIELD_TYPE,
  FormActionConfigInput,
  FormDefinitionSection,
  FormActionField,
  FormDefinitionContent,
  FormActionInvocationParameter,
  FormDefinitionField,
  FormDesignerAction,
  FormFieldType,
  FormSectionType,
  FormDesignerSection,
  FormAction,
  ProcessTaskSchema,
} from 'forms/form-designer/Constants';
import {CREATE_TASK_ACTION} from 'forms/form-designer/FormDesigner';
import {FormFixUpdate, getFormDefinitionUpdates} from 'forms/form-designer/FormFixBanner';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {FIELD_TYPE_TO_LEAF_TYPE} from 'schemas/fields/SchemaFieldConstants';

type FormDefinitionVersion = ExtractByTypename<
  FormDefinitionQuery['formDefinition'],
  'FormDefinitionNode'
>['latestFormDefinitionVersion'];

type FormDefinitionActionConfig = FormDefinitionVersion['actionConfigs'][number];

export interface FormDesignerState {
  sections: Array<FormDesignerSection>;
  actionMap: Map<string, FormDesignerAction>;
}

export const EMPTY_ACTION: FormDesignerAction = {
  key: 'blank',
  selectedAction: undefined,
  selectedSchema: undefined,
  fields: [],
};

export const EMPTY_SECTION: FormDesignerSection = {
  key: 'blank',
  name: '',
  sectionType: FormSectionType.STATIC,
  isIterable: false,
  isNameVisible: false,
};

export const FormDesignerStateActions = {
  // ACTIONS + SCHEMAS
  addAction:
    () =>
    (state: FormDesignerState): FormDesignerState =>
      produce(state, (draft) => {
        const key = uuidv4();
        draft.actionMap.set(key, {...EMPTY_ACTION, key});
      }),
  removeAction:
    (actionKey: uuid) =>
    (state: FormDesignerState): FormDesignerState =>
      produce(state, (draft) => {
        draft.actionMap.delete(actionKey);
      }),
  selectAction:
    (actionKey: uuid, action: Action, options?: {lockAction?: boolean}) =>
    (state: FormDesignerState): FormDesignerState =>
      produce(state, (draft) => {
        draft.actionMap.set(actionKey, {
          key: actionKey,
          selectedAction: action,
          selectedSchema: undefined,
          fields: [],
          lockAction: options?.lockAction,
        });
      }),
  updateAction:
    (actionKey: uuid, actionUpdate: Partial<FormDesignerAction>) =>
    (state: FormDesignerState): FormDesignerState =>
      produce(state, (draft) => {
        draft.actionMap.set(actionKey, {...original(draft.actionMap)!.get(actionKey)!, ...actionUpdate});
      }),
  /** Take a FormAction and use it to create the fields needed for the Form Designer */
  loadAction:
    (actionKey: uuid, formAction: FormAction) =>
    (state: FormDesignerState): FormDesignerState =>
      produce(state, (draft) => {
        const action = draft.actionMap.get(actionKey)!;
        action.fields = formAction.invocationParameters.map((invocationParam) => {
          const {name, isRequired} = invocationParam;
          return {
            key: uuidv4(),
            included: true,
            required: isRequired,
            displayName: name,
            description: null,
            sectionKey: action.sectionKey || state.sections[state.sections.length - 1].key,
            actionParameter: invocationParam,
          };
        });
      }),
  selectSchema:
    (actionKey: uuid, schema: ProcessTaskSchema, options?: {lockSchema?: boolean}) =>
    (state: FormDesignerState): FormDesignerState =>
      produce(state, (draft) => {
        const action = draft.actionMap.get(actionKey)!;
        action.selectedSchema = schema;
        action.lockSchema = options?.lockSchema;
        action.fields = [];
      }),
  // SECTIONS
  addSection:
    () =>
    (state: FormDesignerState): FormDesignerState =>
      produce(state, (draft) => {
        draft.sections.unshift({...EMPTY_SECTION, key: uuidv4()});
      }),
  removeSection:
    (sectionKey: uuid) =>
    (state: FormDesignerState): FormDesignerState => {
      const sectionIndex = state.sections.findIndex((section) => section.key === sectionKey);
      assert(sectionIndex !== -1, `Couldn't find section with key '${sectionKey}'`);
      return produce(state, (draft) => {
        // remove the section from the list
        draft.sections.splice(sectionIndex, 1);
        // move all fields from that section to the next closest section above (below if it was the first section)
        const sectionToMoveTo = draft.sections[sectionIndex === 0 ? 0 : sectionIndex - 1];
        for (const action of draft.actionMap.values()) {
          for (const field of action.fields) {
            if (field.sectionKey === sectionKey) {
              field.sectionKey = sectionToMoveTo.key;
            }
          }
        }
      });
    },
  updateSection:
    (sectionKey: uuid, sectionUpdate: Partial<FormDesignerSection>) =>
    (state: FormDesignerState): FormDesignerState => {
      const sectionIndex = state.sections.findIndex((section) => section.key === sectionKey);
      assert(sectionIndex !== -1, `Couldn't find section with key '${sectionKey}'`);
      return produce(state, (draft) => {
        draft.sections[sectionIndex] = {...state.sections[sectionIndex], ...sectionUpdate};
      });
    },
  updateSections:
    (sections: Array<FormDesignerSection>) =>
    (state: FormDesignerState): FormDesignerState =>
      produce(state, (draft) => {
        draft.sections = sections;
      }),
  // FIELDS
  updateField:
    (fieldKey: uuid, fieldUpdate: FormActionField) =>
    (state: FormDesignerState): FormDesignerState =>
      produce(state, (draft) => {
        const action = draft.actionMap.get(fieldKey)!;
        const indexToUpdate = original(action.fields)!.findIndex((field) => field.key === fieldUpdate.key);
        action.fields[indexToUpdate] = fieldUpdate;
      }),
  updateFields:
    (actionKey: uuid, fieldUpdates: Array<FormActionField>) =>
    (state: FormDesignerState): FormDesignerState =>
      produce(state, (draft) => {
        const action = draft.actionMap.get(actionKey)!;
        action.fields = fieldUpdates;
      }),

  // READ-ONLY ACTIONS

  /** Get all fields in the state across all actions */
  getFields: (state: FormDesignerState): Array<FormActionField> =>
    [...state.actionMap.values()].flatMap((action) => action.fields),
  /** Convert the state to the standard serialized FormDefinition */
  generateFormDefinition: (state: FormDesignerState): FormDefinitionContent => {
    const {sections} = state;
    // Get unnested list of all included form action fields
    const formActionFields = FormDesignerStateActions.getFields(state).filter((field) => field.included);

    function convertFormActionFieldToFormDefinitionField(field: FormActionField): FormDefinitionField {
      const fieldType =
        BNCH_TYPE_DEF_TO_FORM_FIELD_TYPE[
          FIELD_TYPE_TO_LEAF_TYPE[field.actionParameter.parameterTypeName as FieldType]
        ] ?? FormFieldType.BOOLEAN;
      return {
        type: 'FormField',
        key: field.key,
        label: field.displayName,
        matchesSchemaFieldName: field.displayName === field.actionParameter.name,
        description: field.description,
        isRequired: field.required,
        schemaFieldSelectorId:
          fieldType === FormFieldType.DROPDOWN_OPTION
            ? (field.actionParameter.schemaFieldSelectorId ?? undefined)
            : undefined,
        schemaId: field.actionParameter.linkedSchemaId ?? undefined,
        fieldType,
      };
    }

    return {
      sections: sections.map((section) => ({
        ...section,
        type: 'FormSection',
        // Get all form fields assigned to this section and convert to FormDefinitionFields
        content: formActionFields
          .filter((field) => field.sectionKey === section.key)
          .map((field): FormDefinitionField => convertFormActionFieldToFormDefinitionField(field)),
      })),
    };
  },
  /** Convert the state to a set of FormActionConfigs that define how the form is processed on submit */
  generateFormActionConfigs: (state: FormDesignerState): Array<FormActionConfigInput> => {
    const {actionMap} = state;

    return [...actionMap.values()]
      .filter((action) => action.selectedAction)
      .map((action) => ({
        identifier: action.selectedAction!.identifier,
        actionVersion: action.selectedAction!.version,
        schemaId: action.selectedSchema?.id,
        sectionKey: action.sectionKey,
        // We submit all parameters even if the form fields is not included.
        // If the form field is not included, do not submit parameter's fieldKey.
        parameters: action.fields.map((field) => ({
          actionParameterId: field.actionParameter.id,
          formFieldKey: field.included ? field.key : null,
        })),
      }));
  },
};

export interface FormDefinitionFieldWithSectionKey extends FormDefinitionField {
  sectionKey: string;
}

/**
 * Merges a form definition field with its associated action invocation parameter.
 * @returns form action field that is up to date with the schema field.
 */
function createMergedFormActionField(
  field: FormDefinitionFieldWithSectionKey,
  formActionInvocationParameter: FormActionInvocationParameter
): FormActionField {
  // Merge existing form field with existing schema field.
  return {
    key: field.key,
    included: true,
    required: field.isRequired || formActionInvocationParameter.isRequired,
    // Use schema field name if the field used to match it, otherwise use override label
    displayName: field.matchesSchemaFieldName ? formActionInvocationParameter.name : field.label,
    description: field.description,
    sectionKey: field.sectionKey,
    actionParameter: formActionInvocationParameter,
  };
}

/**
 * @returns Form action field created from action invocation parameter.
 */
function createFormActionField(
  formActionInvocationParameter: FormActionInvocationParameter,
  defaultSectionKey: string
): FormActionField {
  return {
    key: uuidv4(),
    included: formActionInvocationParameter.isRequired,
    required: formActionInvocationParameter.isRequired,
    displayName: formActionInvocationParameter.name,
    description: null,
    sectionKey: defaultSectionKey,
    actionParameter: formActionInvocationParameter,
  };
}

/**
 * @returns Form designer action created from potentially out-of-date form definition fields and action config and an up-to-date form action.
 */
function createFormDesignerAction(
  formFields: Array<FormDefinitionFieldWithSectionKey>,
  actionConfig: FormDefinitionActionConfig,
  formAction: FormAction,
  lockAction: boolean,
  lockSchema: boolean,
  defaultSectionKey: string
): FormDesignerAction {
  const actionFields: Array<FormActionField> = [];

  // Add form action fields for existing form fields and ensure they are up to date with their associated schema field.
  for (const actionParameter of actionConfig.parameters) {
    const field = formFields.find((field) => field.key === actionParameter.formFieldKey);

    const formActionInvocationParameter = formAction.invocationParameters.find(
      (invocParam) => invocParam.id === actionParameter.actionParameterId
    );

    if (field && formActionInvocationParameter) {
      actionFields.push(createMergedFormActionField(field, formActionInvocationParameter));
    }
    // else, remove form field since it has no associated schema field.
  }

  // Add form action fields for new schema fields.
  const fieldParameterIds = new Set(actionFields.map((field) => field.actionParameter.id));
  const missingInvocationParams = formAction.invocationParameters.filter(
    (invocParam) => !fieldParameterIds.has(invocParam.id)
  );
  for (const invocParam of missingInvocationParams) {
    actionFields.push(createFormActionField(invocParam, defaultSectionKey));
  }

  return {
    // TODO: get selectedAction using action.actionId when we support actions other than CREATE_TASK_ACTION
    selectedAction: CREATE_TASK_ACTION,
    selectedSchema: actionConfig.schema!,
    key: uuidv4(),
    lockAction,
    lockSchema,
    sectionKey: actionConfig.sectionKey || undefined,
    fields: actionFields,
  };
}

export function unnestFieldsFromSections(
  section: FormDefinitionSection
): Array<FormDefinitionFieldWithSectionKey> {
  return section.content.flatMap((element) =>
    element.type === 'FormSection'
      ? unnestFieldsFromSections(element)
      : {
          ...element,
          sectionKey: section.key,
        }
  );
}

export function convertFormDefinitionToFormDesignerState(
  formDefinitionVersion: FormDefinitionVersion,
  formActions: Array<FormAction>,
  options: {lockAction: boolean; lockSchema: boolean} = {lockAction: false, lockSchema: false}
): [state: FormDesignerState, autofixUpdates: Array<FormFixUpdate>] {
  // Return recursively flattened list of FormDefinitionFields within section, along with parent section key

  const definitionSections: Array<FormDefinitionSection> = formDefinitionVersion.definition.sections;
  const designerSections = definitionSections.map(
    (section): FormDesignerSection => ({
      key: section.key,
      name: section.name,
      sectionType: section.sectionType,
      isIterable: section.isIterable,
      isNameVisible: section.isNameVisible,
      description: section.description,
    })
  );
  const allDefinitionFields = definitionSections.flatMap((section) => unnestFieldsFromSections(section));

  // The section where non-included fields are added to by default.
  const defaultSectionKey = designerSections[0].key;

  const actionMap: Map<string, FormDesignerAction> = new Map();
  const updates: Array<FormFixUpdate> = [];

  for (const [index, actionConfig] of formDefinitionVersion.actionConfigs.entries()) {
    const formAction = formActions[index];
    const designerAction = createFormDesignerAction(
      allDefinitionFields,
      actionConfig,
      formAction,
      options.lockAction,
      options.lockSchema,
      defaultSectionKey
    );
    actionMap.set(designerAction.key, designerAction);

    updates.push(
      ...getFormDefinitionUpdates(
        allDefinitionFields,
        actionConfig.parameters,
        formAction.invocationParameters
      )
    );
  }

  return [
    {
      actionMap,
      sections: designerSections,
    },
    updates,
  ];
}

export interface InitialFormDesignerAction {
  action: Action;
  formAction?: FormAction;
  /** prevent editing of the action */
  lockAction?: boolean;
  schema?: ProcessTaskSchema;
  /** prevent editing of the schema */
  lockSchema?: boolean;
}

export interface FormDesignerStateInitializationOptions {
  initialActions?: Array<InitialFormDesignerAction>;
}

export function initializeFormDesignerState(
  options?: FormDesignerStateInitializationOptions
): FormDesignerState {
  const {initialActions} = options ?? {};
  let state: FormDesignerState = {
    sections: [],
    actionMap: new Map(),
  };

  if (initialActions?.length) {
    for (const [index, initialAction] of initialActions.entries()) {
      // create an iterable section for the action
      state = FormDesignerStateActions.addSection()(state);
      const sectionKey = state.sections[0].key;
      state = FormDesignerStateActions.updateSection(sectionKey, {
        isIterable: true,
        sectionType: FormSectionType.ITERABLE,
      })(state);
      // add the action and assign it to the created section
      state = FormDesignerStateActions.addAction()(state);
      const actionKey = [...state.actionMap.keys()][index];
      state = FormDesignerStateActions.selectAction(actionKey, initialAction.action, {
        lockAction: initialAction.lockAction,
      })(state);
      state = FormDesignerStateActions.updateAction(actionKey, {sectionKey})(state);
      if (initialAction.schema) {
        state = FormDesignerStateActions.selectSchema(actionKey, initialAction.schema, {
          lockSchema: initialAction.lockSchema,
        })(state);
        if (initialAction.formAction) {
          state = FormDesignerStateActions.loadAction(actionKey, initialAction.formAction)(state);
        }
      }
    }
  } else {
    // if the form isn't populated with any initial actions, just add an empty section and action
    state = FormDesignerStateActions.addSection()(state);
    state = FormDesignerStateActions.addAction()(state);
    state = FormDesignerStateActions.updateAction([...state.actionMap.keys()][0], {
      sectionKey: state.sections[0].key,
    })(state);
  }
  return state;
}

/**
 * Checks whether the two given FormDesignerStates are functionally equivalent, accounting for random
 * action keys and missing section descriptions.
 */
export function areDesignerStatesEqual(state1: FormDesignerState, state2: FormDesignerState): boolean {
  function serialize(state: FormDesignerState): object {
    return {
      actions: [...state.actionMap.values()].map((action) => ({
        ...action,
        // Action keys are initially randomized, so ignore action keys for equality
        key: null,
        fields: action.fields.map((field) => ({
          ...field,
          // Keys for non-included fields are initially randomized, so ignore them for equality
          key: field.included ? field.key : null,
        })),
      })),
      sections: state.sections.map((section) => ({
        ...section,
        // Section descriptions are missing when not defined, so set to null
        description: section.description ?? null,
      })),
    };
  }
  return state1 === state2 || isEqual(serialize(state1), serialize(state2));
}
